import * as React from 'react';
import { useState, useContext, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import {
    AppBar,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    MenuItem,
    Popover,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Toolbar,
    Tooltip,
    Checkbox
} from '@material-ui/core';
import {
    ArrowDownward,
    ArrowUpward,
    Clear,
    Done,
    GetApp,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    Loop,
    PictureAsPdf,
    Print,
    Search,
    Today
} from '@material-ui/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { DateTime } from 'luxon';
import * as Styled from './styled';
import { Client, ElectronDialogReturn, Matter, MatterTypeText, PdfFormatType, TimeEntrySortType, TimeKeeperAssignment } from '../../api/types/types';
import AutoCompleteField from 'components/AutoCompleteField/AutoCompleteField';
import { MATTER_TYPE } from 'store/timeentry.store';
import TimeEntryList from 'components/TimeEntryList/TimeEntryList';
import TimeEntry, { SapStatus } from 'api/immutables/ImmutableTimeEntry';
import { InlineDatePicker } from 'material-ui-pickers';
import { StandardTextFieldProps } from '@material-ui/core/TextField';
import { FlexDiv } from 'common/flex';
import ImmutableTimeEntry from '../../api/immutables/ImmutableTimeEntry';
import Pagination from '../Pagination/Pagination';
import ExcelIcon from '../../images/excel.png';
import { exportToXLSX, toExcelFormat } from '../../util/ExportToExcel';
import { renderToStaticMarkup } from 'react-dom/server';
import PrintAsTimeEntryPdfExport from '../TimeEntries/PrintAsTimeEntryPdfExport';
import printJS from 'print-js';
import * as StyledPdf from '../TimeEntries/styled.pdf';
import { fileNameForExport, systemTimeZone } from '../../util/utils';
import { buildPdf } from '../../util/SaveAsTimeEntryPDF';
import { Platform } from '../../util/Platform';
import { isoDate, getDateFormat } from '../../util/date';
import { FabContainerView } from 'components/FabContainer/FabContainerView';
import { TKConsumer } from 'common/TKProvider';
import { RootStoreContext } from '../../App';
import BulkUploadButton from 'containers/BulkUpload/BulkUploadButton';
import BulkTemplateDownloadButton from 'containers/BulkUpload/BulkTemplateDownloadButton';
import { LoadingDiv } from 'containers/Home/Home';
import { theme } from 'common/theme';
import { getMatterTooltipText } from 'util/timeEntry';
import { MatterItemFormatter } from 'components/TemplateForm/TemplateForm';
const { STYLES_FOR_PDF } = StyledPdf;

const TimeEntries = () => {
    const [popOverPdfEl, setPopOverPdfEl] = useState<HTMLElement | null>(null);
    const [pdfFormatTypeValue, setPdfFormatTypeValue] = useState<PdfFormatType>(PdfFormatType.DATETYPE);
    const entriesListRef = useRef<HTMLDivElement>(null);
    const fromDateRef = useRef<HTMLDivElement>(null);
    const untilDateRef = useRef<HTMLDivElement>(null);
    const rootStore = useContext(RootStoreContext);
    const { t } = useTranslation([ 'timeentries', 'common' ]);
    const openedPdfEl = Boolean(popOverPdfEl);
    const {
        features,
        isProject,
        isEwrEnvironment,
        getActiveTimeKeeper,
        setPreviousActiveElement
    } = rootStore.appStore;
    const matterLabel = features.EpochConfigMatterLabel;
    const billableTextKey = isProject || isEwrEnvironment ? 'billable' : 'matter_type';

    const {
        dirty,
        fromDate,
        untilDate,
        status,
        matterType,
        selectedEntryIds,
        expandedEntryIds,
        searchText,
        client,
        matter,
        loading,
        noEntries,
        validationState,
        referenceTE,
        durVstate,
        allEntriesSelected,
        filteredEntries,
        serverTimeEntries,
        currentPage,
        entriesPerPage,
        matterLoading,
        groupEntriesByClient,
        groupEntriesByMatter,
        timeEntriesMapForPDF,
        showOnlyRejectedEntries,
        loadEntry,
        getEntries,
        setTimeEntryRange,
        setReferenceTE,
        setClient,
        setMatter,
        setMatterType,
        setBillable,
        setStatus,
        setSearchText,
        setPageNum,
        setTimeEntryDateRange,
        setEntriesPerPage,
        setFieldLoaderFn,
        setSelectedTimeEntries,
        setExpandedTimeEntries,
        saveEntries,
        postEntries,
        unpostEntries,
        copyTimeEntry,
        revertEntry,
        splitEntry,
        mergeEntries,
        transferEntries,
        deleteEntries,
        changeEntry,
        saveDirtyEntries,
        postSelectedEntries,
        moveSelectedEntries,
        deleteSelectedEntries,
        determineCodeSets,
        toggleSelectAllFlag,
        toggleShowOnlyRejectedEntries,
        openNewTimeEntryDialog,
        getAggregateTotalsFor,
        resetTimeEntryStore,
        timeEntrySortType,
        saveTimeEntrySortType,
        loadTimeEntrySortType,
        isAscending,
        toggleSortDirection
    } = rootStore.timeEntryStore;
    let fieldSize: 3 | 4 = (matter && matter.id === -1) ? 3 : 4;

    useEffect(() => {
        let loc = DateTime.local().setZone(systemTimeZone);
        let _fromDate = DateTime.utc(loc.year, loc.month, loc.day)
            .minus({ days: features.EpochConfigTimeEntriesPageDays ? features.EpochConfigTimeEntriesPageDays : 7 });
        let _untilDate = DateTime.utc(loc.year, loc.month, loc.day);

        loadTimeEntrySortType();
        setTimeEntryDateRange();
        setTimeEntryRange(_fromDate, _untilDate);
        getEntries();
        rootStore.timeEntryStore.expandedEntryIds = [];
        return () => {
            resetTimeEntryStore();
        }
    }, []);

    const handleFromDateChange = (d: Date) => {
        const workDate: DateTime = DateTime.local(
            d.getFullYear(),
            d.getMonth() + 1,
            d.getDate(),
            0,
            0,
            0
        );

        let _fromDate = workDate;
        let _untilDate = untilDate;
        if (_untilDate <= workDate) {
            _untilDate = workDate;
        }
        setTimeEntryRange(_fromDate, _untilDate);
        setTimeout(() => fromDateRef.current!.focus(), 0);
    }
    const handleUntilDateChange = (d: Date) => {
        const workDate: DateTime = DateTime.local(
            d.getFullYear(),
            d.getMonth() + 1,
            d.getDate(),
            0,
            0,
            0
        );

        let _fromDate = fromDate;
        let _untilDate = workDate;
        if (_fromDate >= _untilDate) {
            _fromDate = workDate;
        }
        setTimeEntryRange(_fromDate, _untilDate);
        setTimeout(() => untilDateRef.current!.focus(), 0);
    }
    const handleClientChange = (c: Client | null | undefined) => {
        setClient(c);
        handleMatterChange(null);
    }
    const handleMatterChange = (m: Matter | null | undefined) => {
        changePage(1);
        rootStore.timeEntryStore.selectedEntryIds = [];
        setMatter(m);
        setReferenceTE(null);
    }
    const handleSearchTextChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        changePage(1);
        setSearchText(evt.target.value);
    }
    const handleStatusChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
        changePage(1);
        setStatus(evt.target.value);
    }
    const handleTimeEntrySortTypeChange = (evt: React.ChangeEvent<{ value: unknown }>) => {
        saveTimeEntrySortType(evt.target.value as TimeEntrySortType);
    }
    const handleMatterTypeChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
        const value = evt.target.value;
        changePage(1);
        setMatterType(value);
        if (isProject || isEwrEnvironment) {
            _setBillable(value);
        }
    }
    const _setBillable = (val: string) => {
        if (MATTER_TYPE[val] === MATTER_TYPE.BILLABLE) {
            setBillable(true);
        } else if (MATTER_TYPE[val] === MATTER_TYPE.NON_BILLABLE) {
            setBillable(false);
        } else {
            setBillable(undefined);
        }
    }
    const reset = () => {
        let loc = DateTime.local();
        setSearchText('');
        setStatus('All');
        setMatterType(MATTER_TYPE.All);
        setTimeEntryDateRange();
        setClient(null);
        handleMatterChange(null);
        getEntries();
        rootStore.timeEntryStore.expandedEntryIds = [];
        // resetting pagination values
        setPageNum(1);
        setEntriesPerPage(25);
    }
    const search = () => {
        getEntries();
        setPageNum(1);
    }
    const createEntry = (date: DateTime) => {
        setPreviousActiveElement();
        openNewTimeEntryDialog(date);
    }
    const editEntry = async (entry: TimeEntry) => {
        await loadEntry(entry.clone());
    }
    const deleteEntry = (entry: TimeEntry) => {
        deleteEntries([entry]);
    }
    const saveEntry = (entry: TimeEntry) => {
        saveEntries([entry]);
    }
    const postEntry = async (entry: TimeEntry) => {
        if (!expandedEntryIds.includes(entry.id!)) {
            await determineCodeSets(entry.id!);
        }
        postEntries([entry.id!]);
    }
    const unpostEntry = async (entry: TimeEntry) => {
        unpostEntries([entry.id!]);
    }
    const clearSelectedEntries = () => {
        setSelectedTimeEntries([]);
    }
    const fetchRefs = async (text: string) => {
        let ret: TimeEntry[] = [];
        const uniqueRet = new Map();
        
        serverTimeEntries.forEach((entry) => {
            uniqueRet.set(entry.reference, entry);
        });
        uniqueRet.forEach((te, k) => {
            if (k) { ret.push(te); }
        });
        return ret;
    }
    const exportToExcel = async () => {
        const currentTimeKeeper = getActiveTimeKeeper();
        
        const fileName = fileNameForExport(currentTimeKeeper, t('export.pdf.title'));
        
        const sortByDateEntries = filteredEntries.sort((e1, e2) => {
            if (DateTime.fromISO(e1.workDateTime) > DateTime.fromISO(e2.workDateTime)) { return 1; }
            if (DateTime.fromISO(e1.workDateTime) < DateTime.fromISO(e2.workDateTime)) { return -1; }
            return 0;
        });
        const excelEntries = toExcelFormat(sortByDateEntries, false, t);
        
        exportToXLSX(excelEntries, fileName, t, false);
    }
    const customizeHeadersForPdf = (): Map<string, ImmutableTimeEntry[]> => {
        let timeEntriesMap = new Map<string, ImmutableTimeEntry[]>();

        switch (pdfFormatTypeValue) {
            case PdfFormatType.CLIENTTYPE:
                timeEntriesMap = groupEntriesByClient;
                break;
            case PdfFormatType.MATTERTYPE:
                timeEntriesMap = groupEntriesByMatter;
                break;
            case PdfFormatType.DATETYPE:
                [...timeEntriesMapForPDF.entries()].sort()
                    .forEach(([date, tentries]) => {
                        let d = DateTime.fromISO(date).toFormat('DDDD');
                        timeEntriesMap.set(d, tentries);
                    });
                break;
            default:
                timeEntriesMap = timeEntriesMapForPDF;
                break;
        }
        
        return timeEntriesMap;
    }
    const printPDF = () => {
        const currentTimeKeeper = getActiveTimeKeeper();
        const fileName = fileNameForExport(currentTimeKeeper, t('export.pdf.title'));

        let timeEntriesMap = customizeHeadersForPdf();

        let html = renderToStaticMarkup(
            <PrintAsTimeEntryPdfExport
                timeEntriesMap={timeEntriesMap}
                exportType={pdfFormatTypeValue}
                timeKeeper={currentTimeKeeper ? currentTimeKeeper.name : ''}
                fromDate={fromDate}
                toDate={untilDate}
                isMgmtDashboard={false}
            />);
        
        let pdfDiv = new DOMParser().parseFromString(html, 'text/html');
        let pdfContainer = document.createElement('div');
        
        if (pdfDiv.body.firstChild) {
            pdfContainer.appendChild(pdfDiv.body.firstChild);
            pdfContainer.setAttribute('style', 'visibility: hidden')
            document.body.appendChild(pdfContainer);
            printJS({
                printable: 'printjs-form',
                type: 'html',
                documentTitle: t('export.pdf.title'),
                style: STYLES_FOR_PDF,
                onLoadingStart: () => { document.title = fileName; },
                onPrintDialogClose: () => { document.title = 'Epoch'; },
                honorColor: true,
                honorMarginPadding: true
            });
            
            document.body.removeChild(pdfContainer);
        }
        
        handleClosePdfPopOver();
    }
    const saveAsPDF = async () => {
        const currentTimeKeeper = getActiveTimeKeeper();
        const timeEntriesMap = customizeHeadersForPdf();
        const fileName = fileNameForExport(currentTimeKeeper, t('export.pdf.title'));

        let doc = await buildPdf(
            timeEntriesMap,
            pdfFormatTypeValue,
            currentTimeKeeper ? currentTimeKeeper.name : '',
            fromDate,
            untilDate,
            false,
            t
        );
        
        if (Platform.isElectron()) {
            let pdfString = doc.output('datauristring');
            const {dialog} = require('@electron/remote');
            const fs = require('fs-jetpack');
            let dataUriToBuffer = require('data-uri-to-buffer');
            const path = require('path');
            const bufferData = dataUriToBuffer(pdfString);

            dialog.showSaveDialog({
                    defaultPath: fileName,
                    filters: [{
                            name: 'pdf',
                            extensions: ['pdf']
                    }]
                }).then((file: ElectronDialogReturn) => {
                    if (file.canceled) {
                        return;
                    }
                    fs.write(
                        path.normalize(file.filePath),
                        bufferData
                    );
                }
            );
        } else {
            doc.save(`${fileName}.pdf`);
        }
        
        handleClosePdfPopOver();
    }
    const openPdfPopOver = async (evt: React.MouseEvent<HTMLElement>) => {
        setPopOverPdfEl(evt.currentTarget);
    }
    const handleClosePdfPopOver = () => {
        setPopOverPdfEl(null);
    }
    const handlePdfFormatTypeChange = (event: React.ChangeEvent<HTMLFormElement>, val: string) => {
        setPdfFormatTypeValue(val as PdfFormatType);
    }
    const changePage = (pageNum: number) => {
        setPageNum(pageNum);
        entriesListRef.current!.scrollTop = 0;
    }
    const changeEntriesPerPage = (n: number) => {
        setEntriesPerPage(n);
        changePage(1);
    }
    const fetchMatters = async (text: string, offset: number, limit: number, clientId: number | undefined) => {
        let  results: Matter[] = await rootStore.api.Matter.searchMatters(text, true, clientId!, undefined, offset, limit);

        if (!features.EpochConfigTrackedMatterClientsEnabled && results.length === 0) {
            results = await rootStore.api.Matter.getAvailableMatters(text, false, clientId!, undefined, offset, limit);
        }
        return results;
    }
    const handleRejectedChange = () => {
        changePage(1);
        toggleShowOnlyRejectedEntries();
    }

    return (
        <>
            <AppBar position={'static'} style={{background: theme.header}}>
                <Toolbar variant={'regular'}>
                    <Grid container={true} xs={12}>
                        <Styled.StyledGrid
                            container={true}
                            xs={12}
                            spacing={8}
                            disabled={loading}
                        >
                            <Grid item={true} xs={2} md={(fieldSize - 1) as 2 | 3}>
                                <TextField
                                    id={'input-with-icon-textfield'}
                                    label={t('header.field.search.label')}
                                    placeholder={t('header.field.search.place_holder')}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position={'start'}>
                                                <Search/>
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchText ? (
                                            <InputAdornment position={'end'} onClick={() => rootStore.timeEntryStore.setSearchText('')}>
                                                <Clear style={{ width: 20, cursor: 'pointer' }} />
                                            </InputAdornment>
                                        ) : null
                                    }}
                                    value={searchText}
                                    fullWidth={true}
                                    onChange={handleSearchTextChange}
                                    inputProps={{
                                        'data-auto-focus': true
                                    }}
                                />
                            </Grid>
                            <Grid item={true} xs={2}>
                                <FormControl fullWidth={true}>
                                    <InputLabel shrink={true}>
                                        {t('header.sort_by.tooltip')}
                                    </InputLabel>
                                    <Select
                                        value={timeEntrySortType}
                                        onChange={handleTimeEntrySortTypeChange}
                                    >
                                        {(Object.keys(TimeEntrySortType) as TimeEntrySortType[]).map((val) => (
                                            <MenuItem value={val} key={val}>
                                                {t(`header.sort_by.options.${val.toLowerCase()}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item={true}>
                                <FlexDiv style={{ marginTop: 7 }}>
                                    <Tooltip
                                        title={t(`header.sort_by.icon.${isAscending ? 'ascending' : 'descending'}.tooltip`)}
                                    >
                                        <IconButton onClick={toggleSortDirection}>
                                            {isAscending ? <ArrowUpward /> : <ArrowDownward />}
                                        </IconButton>
                                    </Tooltip>
                                </FlexDiv>
                            </Grid>
                            <Grid item={true} xs={2}>
                                <InlineDatePicker
                                    format={fromDate.toFormat(getDateFormat())}
                                    value={isoDate(fromDate)}
                                    label={t('header.field.date.from.label')}
                                    onChange={handleFromDateChange}
                                    leftArrowIcon={<KeyboardArrowLeft/>}
                                    rightArrowIcon={<KeyboardArrowRight/>}
                                    InputProps={{
                                        endAdornment: (
                                        <InputAdornment position={'end'} >
                                            <IconButton
                                                buttonRef={fromDateRef}
                                                aria-label={t('header.field.date.from.aria_label', { date: fromDate.toFormat(getDateFormat()) })}
                                                tabIndex={0}
                                            >
                                                <Today/>
                                            </IconButton>
                                        </InputAdornment>
                                        ),
                                        inputProps: {
                                            tabIndex: -1
                                        }
                                    }}
                                    TextFieldComponent={
                                        (props: StandardTextFieldProps) =>
                                            <TextField {...props} fullWidth={true}/>
                                    }
                                />
                            </Grid>
                            <Grid item={true} xs={2}>
                                <InlineDatePicker
                                    format={untilDate.toFormat(getDateFormat())}
                                    value={isoDate(untilDate)}
                                    label={t('header.field.date.to.label')}
                                    onChange={handleUntilDateChange}
                                    leftArrowIcon={<KeyboardArrowLeft/>}
                                    rightArrowIcon={<KeyboardArrowRight/>}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position={'end'}>
                                            <IconButton
                                                buttonRef={untilDateRef}
                                                aria-label={t('header.field.date.to.aria_label', { date: untilDate.toFormat(getDateFormat()) })}
                                                tabIndex={0}
                                            >
                                                <Today/>
                                            </IconButton>
                                            </InputAdornment>
                                        ),
                                        inputProps: {
                                            tabIndex: -1
                                        }
                                    }}
                                    TextFieldComponent={
                                        (props: StandardTextFieldProps) =>
                                            <TextField {...props} fullWidth={true}/>
                                    }
                                />
                            </Grid>
                            <Grid item={true}>
                                <FlexDiv style={{marginTop: 7}}>
                                    <Tooltip title={t('header.action.reset.tooltip')}>
                                        <IconButton onClick={reset}>
                                            <Loop />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('header.action.apply_date.tooltip')}>
                                        <IconButton onClick={search}>
                                            <Done />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('header.action.download_excel.tooltip')}>
                                        <IconButton
                                            key={'csv'}
                                            onClick={exportToExcel}
                                            disabled={noEntries}
                                        >
                                            <FontAwesomeIcon style={{width: '24px', padding: '1px 0 2px'}} icon={faFileExcel as IconProp} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('header.action.pdf.tooltip')}>
                                        <IconButton
                                            key={'pdf'}
                                            onClick={openPdfPopOver}
                                            disabled={noEntries}
                                        >
                                            <PictureAsPdf/>
                                        </IconButton>
                                    </Tooltip>
                                    {features.EpochConfigTimeEntryUploadEnabled.includes(Platform.get()) &&
                                        <>
                                            <BulkTemplateDownloadButton />
                                            <BulkUploadButton />
                                        </>
                                    }
                                    <Popover
                                        id="download-pdf-pop"
                                        open={openedPdfEl}
                                        anchorEl={popOverPdfEl}
                                        onClose={handleClosePdfPopOver}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'right',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                    >
                                        <div>
                                            <FormControl style={{width: '140px', padding: '10px'}}>
                                                <FormLabel>{t('header.action.pdf.menu.group_by.label')}:</FormLabel>
                                                <RadioGroup
                                                    aria-label={t('header.action.pdf.menu.group_by.label')}
                                                    name={'groupBy'}
                                                    value={pdfFormatTypeValue}
                                                    onChange={handlePdfFormatTypeChange}
                                                >
                                                    {Object.keys(PdfFormatType)
                                                        .filter(p => PdfFormatType[p] !== PdfFormatType.TIMEKEEPER)
                                                        .map((key) => {
                                                            const typeKey = PdfFormatType[key].toLowerCase().replace(' ', '_');
                                                            const transKey = `header.action.pdf.menu.group_by.option.${typeKey}`;
                                                            return(
                                                                <FormControlLabel
                                                                    key={key}
                                                                    control={<Radio/>}
                                                                    label={t(transKey, {matterLabel})}
                                                                    value={PdfFormatType[key]}
                                                                />
                                                            );
                                                        }
                                                    )}
                                                </RadioGroup>
                                                <Styled.PrintIcons>
                                                    <Tooltip title={t('header.action.pdf.menu.action.print.tooltip')}>
                                                        <IconButton
                                                            onClick={printPDF}
                                                            aria-label={t('header.action.pdf.menu.action.print.tooltip')}
                                                        >
                                                            <Print/>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t('header.action.pdf.menu.action.download.tooltip')}>
                                                        <IconButton
                                                            onClick={saveAsPDF}
                                                            aria-label={t('header.action.pdf.menu.action.download.tooltip')}
                                                        >
                                                            <GetApp/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Styled.PrintIcons>
                                            </FormControl>
                                        </div>
                                    </Popover>
                                </FlexDiv>
                            </Grid>
                            <TKConsumer>
                                {(tk: TimeKeeperAssignment) =>
                                    <Grid item={true}>
                                        {matter && matter.id === -1 ?
                                            <FlexDiv
                                                fillContainer={true}
                                                style={{ marginTop: 5, alignItems: 'center' }}
                                            >
                                                <Tooltip title={t('select_all', { ns: 'common' })}>
                                                    <Checkbox
                                                        checked={allEntriesSelected}
                                                        onChange={toggleSelectAllFlag}
                                                        style={{ padding: 0 }}
                                                        disabled={!tk.writable}
                                                    />
                                                </Tooltip>
                                                &nbsp;
                                                {tk.writable &&
                                                    <span> {t('select_all', { ns: 'common' })} </span>
                                                }
                                            </FlexDiv>
                                        :
                                            null
                                        }
                                    </Grid>
                                }
                            </TKConsumer>
                            <Grid item={true}>
                                {features.EpochConfigManagementDashboard ?
                                    <FlexDiv
                                        fillContainer={true}
                                        style={{ marginTop: 5, alignItems: 'center' }}
                                    >
                                        <Checkbox
                                            checked={showOnlyRejectedEntries}
                                            onChange={handleRejectedChange}
                                            style={{ padding: 0 }}
                                        />
                                        &nbsp;
                                        <span style={{ color: theme.black.dark, opacity: 0.38 }}>{t('header.checkbox.rejected.label')}</span>
                                    </FlexDiv>
                                    :
                                    null
                                }
                            </Grid>
                        </Styled.StyledGrid>
                        <Styled.StyledGrid
                            container={true}
                            xs={12}
                            spacing={8}
                            disabled={loading}
                        >
                            <Grid item={true} xs={2}>
                                <FormControl
                                    fullWidth={true}
                                >
                                    <InputLabel shrink={true}>
                                        {t('header.field.status.label')}
                                    </InputLabel>
                                    <Select
                                        input={<Input name={'status'} id={'status-label-placeholder'}/>}
                                        displayEmpty={true}
                                        name={'select-status'}
                                        SelectDisplayProps={{
                                            'aria-label': `
                                                ${t('header.field.status.label')}:
                                                ${t(`header.field.status.option.${status === SapStatus.UNSUBMITTED ? 'draft' : status.toLowerCase()}`)}
                                            `
                                        }}
                                        value={status}
                                        onChange={handleStatusChange}
                                    >
                                        <MenuItem value={'All'}>{t('header.field.status.option.all')}</MenuItem>
                                        <MenuItem value={SapStatus.POSTED}>{t('header.field.status.option.posted')}</MenuItem>
                                        <MenuItem value={SapStatus.UNSUBMITTED}>{t('header.field.status.option.draft')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item={true} xs={2}>
                                <FormControl
                                    fullWidth={true}
                                >
                                    <InputLabel shrink={true}>
                                        {t(`header.field.${billableTextKey}.label`, {matterLabel})}
                                    </InputLabel>
                                    <Select
                                        input={<Input name={'matter'} id={'matter-label-placeholder'}/>}
                                        displayEmpty={true}
                                        name={'select-matter-type'}
                                        value={matterType}
                                        onChange={handleMatterTypeChange}
                                        SelectDisplayProps={{
                                            'aria-label': `
                                                ${t(`header.field.${billableTextKey}.label`, {matterLabel})}:
                                                ${t(`header.field.${billableTextKey}.option.${matterType.toLowerCase().replace(' ', '_')}`)}
                                            `
                                        }}
                                    >
                                        {Object.keys(MATTER_TYPE).map((key) => {
                                            const typeKey = MATTER_TYPE[key].toLowerCase().replace(' ', '_');
                                            return (
                                                <MenuItem
                                                    key={key}
                                                    value={key}
                                                >
                                                    {t(`header.field.${billableTextKey}.option.${typeKey}`)}
                                                </MenuItem>
                                            )}
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item={true} xs={3} md={fieldSize}>
                                <AutoCompleteField
                                    label={t('field.client')}
                                    fetch={rootStore.api.Client.searchClients}
                                    currentItem={client}
                                    getItemText={(c: Client) => `${c.name} - ${c.number}`}
                                    clearable={true}
                                    onSelect={handleClientChange}
                                    onClear={() => handleClientChange(null)}
                                />
                            </Grid>
                            <Grid item={true} xs={3} md={fieldSize}>
                                <AutoCompleteField
                                    label={t('field.matter', {matterLabel})}
                                    fetch={(text, offset, limit) => fetchMatters(text, offset, limit, client ? client.id : undefined)
                                        .then((res) => {
                                            let blank: Matter = {
                                                id: -1,
                                                number: '',
                                                name: 'Blank',
                                                description: '',
                                                clientId: -1,
                                                isPhaseCode: false,
                                                isActCode: false,
                                                isFfTaskCode: false,
                                                clientName: '',
                                                clientNumber: '',
                                                status: '',
                                                language: '',
                                                languageText: '',
                                                timeEntryUnit: '',
                                                entryType: '',
                                                type: '',
                                                typeText: MatterTypeText.NON_BILLABLE,
                                                statusDescription: '',
                                                lastModified: '',
                                                startDate: '',
                                                endDate: '',
                                                bannedWords: [],
                                                blockBillingWords: []
                                            };
                                            if (!client && !text) {
                                                res.unshift(blank)
                                            }
                                            return res;
                                        }
                                    )}
                                    currentItem={matter}
                                    formatItem={(m: Matter, menuItem: boolean) => MatterItemFormatter(m, menuItem, true)}
                                    getItemText={(m: Matter) => (m.id === -1) ? m.name : `${m.name} - ${m.number}`}
                                    clearable={true}
                                    onSelect={handleMatterChange}
                                    onClear={() => handleMatterChange(null)}
                                    tooltip={getMatterTooltipText}
                                />
                            </Grid>
                            {matter && matter.id === -1 &&
                                <Grid item={true} xs={2} lg={2} md={2}>
                                    <AutoCompleteField
                                        label={t('field.reference')}
                                        fetch={fetchRefs}
                                        currentItem={referenceTE}
                                        getItemText={(entry: TimeEntry) => entry.reference!}
                                        clearable={true}
                                        onSelect={setReferenceTE}
                                        onClear={() => setReferenceTE(null)}
                                    />
                                </Grid>
                            }
                        </Styled.StyledGrid>
                    </Grid>
                </Toolbar>
            </AppBar>
            <Pagination
                entriesPerPage={entriesPerPage}
                currentPage={currentPage}
                entriesLength={filteredEntries.length}
                changeEntriesPerPage={changeEntriesPerPage}
                onPageChange={changePage}
            />
            <Styled.TimeEntryList innerRef={entriesListRef}>
                {[...rootStore.timeEntryStore.timeEntriesMap].map(([date, entries]) => (
                    <TimeEntryList
                        entries={entries}
                        validationMap={validationState}
                        durValidationMap={durVstate}
                        date={DateTime.fromISO(date)}
                        onChange={changeEntry}
                        selected={selectedEntryIds}
                        onSelect={setSelectedTimeEntries}
                        expanded={expandedEntryIds}
                        onExpand={setExpandedTimeEntries}
                        onCreateEntry={createEntry}
                        onPost={postEntry}
                        onUnpost={unpostEntry}
                        onSave={saveEntry}
                        onDelete={deleteEntry}
                        onCopy={copyTimeEntry}
                        onEdit={editEntry}
                        onCancel={revertEntry}
                        onSplit={splitEntry}
                        onMerge={mergeEntries}
                        onTransfer={transferEntries}
                        buildCodeSets={determineCodeSets}
                        aggregateTotals={getAggregateTotalsFor}
                        onSetFieldLoader={setFieldLoaderFn}
                    />
                ))}
                {(loading || rootStore.bulkUploadStore.loading) && LoadingDiv(undefined, undefined, true)}
                {noEntries && <Styled.NoEntry>{t('list.empty')}</Styled.NoEntry>}
            </Styled.TimeEntryList>
            <FabContainerView
                dirty={dirty}
                saveDirtyEntries={saveDirtyEntries}
                clearSelectedEntries={clearSelectedEntries}
                postSelectedEntries={postSelectedEntries}
                moveSelectedEntries={moveSelectedEntries}
                mergeEntries={mergeEntries}
                transferEntries={transferEntries}
                deleteSelectedEntries={deleteSelectedEntries}
                selectedEntryIds={selectedEntryIds}
                disableSavePost={matterLoading}
            />
        </>
    );
}

export default observer(TimeEntries);